<template>
    <div class="sj-edit" :class="{ 'sj-edit--light': light }" :style="{ color: textColor }">
        <button v-if="!editing" type="button" class="sj-edit__show" title="Change the jersey for this match" @click="start">
            <span class="sj-edit__number">#{{ shown }}</span>
            <v-icon small :color="textColor">mdi-pencil-outline</v-icon>
            <span v-if="hasAlternative" class="sj-edit__note">official #{{ official }}</span>
        </button>
        <div v-else class="sj-edit__form" @keydown.esc.stop.prevent="cancel">
            <span class="sj-edit__hash">#</span>
            <input
                ref="input"
                v-model="draft"
                class="sj-edit__input"
                inputmode="numeric"
                maxlength="3"
                :placeholder="String(official)"
                @keydown.enter.prevent="save"
            >
            <v-btn icon small :color="textColor" :disabled="!canSave" :loading="saving" title="Save" @click="save">
                <v-icon>mdi-check</v-icon>
            </v-btn>
            <v-btn icon small :color="textColor" :disabled="saving" title="Cancel" @click="cancel">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <button v-if="hasAlternative" type="button" class="sj-edit__reset" :disabled="saving" @click="reset">
                Use official #{{ official }}
            </button>
        </div>
    </div>
</template>

<script>
    // Jersey of the selected player for the live stats: shows the number in use and edits the alternative one.
    // Emits save(value): a string of digits, or null to go back to the official jersey; the parent calls done(ok).
    export default {
        name: 'StatsJerseyEdit',
        props: {
            value: {
                type: [String, Number],
                default: null
            },
            official: {
                type: [String, Number],
                default: null
            },
            textColor: {
                type: String,
                default: 'inherit'
            },
            // light background: dark borders
            light: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            editing: false,
            saving: false,
            draft: ''
        }),
        computed: {
            hasAlternative() {
                return this.value !== null && this.value !== undefined && this.value !== '' && String(this.value) !== String(this.official)
            },
            shown() {
                return this.hasAlternative ? this.value : this.official
            },
            cleanDraft() {
                return String(this.draft || '').trim()
            },
            // digits only, and something different from what is in use
            canSave() {
                return /^\d{1,3}$/.test(this.cleanDraft) && this.cleanDraft !== String(this.shown)
            }
        },
        watch: {
            // another player was selected while editing
            official() {
                this.editing = false
                this.saving = false
            }
        },
        methods: {
            start() {
                this.draft = this.hasAlternative ? String(this.value) : ''
                this.editing = true
                this.$nextTick(() => this.$refs.input && this.$refs.input.focus())
            },
            cancel() {
                if (this.saving) return
                this.editing = false
            },
            save() {
                if (!this.canSave || this.saving) return
                // typing the official number is the same as resetting
                this.emit(this.cleanDraft === String(this.official) ? null : this.cleanDraft)
            },
            reset() {
                this.emit(null)
            },
            emit(value) {
                this.saving = true
                this.$emit('save', value, ok => {
                    this.saving = false
                    if (ok) this.editing = false
                })
            }
        }
    }
</script>

<style scoped>
.sj-edit {
    display: inline-flex;
    align-items: center;
    min-height: 36px;
}

.sj-edit__show {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 18px;
    border: 1px solid rgba(255, 255, 255, 0.45);
    background: rgba(255, 255, 255, 0.12);
    color: inherit;
    cursor: pointer;
}

.sj-edit--light .sj-edit__show {
    border-color: rgba(0, 0, 0, 0.25);
    background: rgba(0, 0, 0, 0.05);
}

.sj-edit__number {
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
}

.sj-edit__note {
    font-size: 12px;
    opacity: 0.8;
}

.sj-edit__form {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px 4px 2px 10px;
    border-radius: 18px;
    border: 1px solid rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.16);
}

.sj-edit--light .sj-edit__form {
    border-color: rgba(0, 0, 0, 0.35);
    background: rgba(0, 0, 0, 0.05);
}

.sj-edit__hash {
    font-size: 16px;
    font-weight: 700;
    opacity: 0.8;
}

.sj-edit__input {
    width: 44px;
    border: none;
    outline: none;
    background: transparent;
    color: inherit;
    font-size: 16px;
    font-weight: 700;
}

.sj-edit__input::placeholder {
    color: inherit;
    opacity: 0.45;
}

.sj-edit__reset {
    margin-left: 4px;
    padding: 4px 8px;
    border-radius: 12px;
    border: none;
    background: rgba(255, 255, 255, 0.18);
    color: inherit;
    font-size: 12px;
    white-space: nowrap;
    cursor: pointer;
}

.sj-edit--light .sj-edit__reset {
    background: rgba(0, 0, 0, 0.07);
}
</style>
